/**
 * Tier Demo Page
 *
 * Interactive testing page for the 3-layer visual identity system.
 * Allows manual testing of:
 * - Tier switching (Layer 1/2/3)
 * - Font loading
 * - Tier-adaptive components
 * - Accessibility features
 *
 * Access: http://localhost:8081/tier-demo (web only)
 */
import { useState } from 'react';
import { View, ScrollView, TouchableOpacity, Platform } from 'react-native';
import { TierButton } from '@dramadetector/ui';
import { TierCard } from '@dramadetector/ui';
import { TierHeadline, TierBody, NarratorText } from '@dramadetector/ui';

export default function TierDemoScreen() {
  const [currentTier, setCurrentTier] = useState<'layer1' | 'layer2' | 'layer3'>('layer1');

  // Only works on web
  const switchTier = (tier: 'layer1' | 'layer2' | 'layer3') => {
    if (Platform.OS === 'web' && typeof document !== 'undefined') {
      document.documentElement.setAttribute('data-tier', tier);
      try {
        localStorage.setItem('userTier', tier);
      } catch (e) {
        console.warn('Failed to persist tier:', e);
      }
      setCurrentTier(tier);
    }
  };

  return (
    <ScrollView className="flex-1 bg-light-gray">
      <View className="p-8 max-w-4xl mx-auto">
        {/* Header */}
        <View className="mb-8">
          <TierHeadline size="4xl" weight="bold" as="h1" testID="demo-title">
            Visual Identity System Demo
          </TierHeadline>
          <TierBody size="lg" color="text-medium-gray" className="mt-2">
            Test the 3-layer tier-adaptive design system
          </TierBody>
        </View>

        {/* Tier Switcher */}
        <TierCard variant="default" className="mb-8">
          <TierHeadline size="2xl" weight="bold" className="mb-4">
            Current Tier: {currentTier.toUpperCase()}
          </TierHeadline>
          <View className="flex-row gap-4">
            <TierButton
              variant={currentTier === 'layer1' ? 'primary' : 'secondary'}
              size="md"
              onPress={() => switchTier('layer1')}
              testID="switch-layer1"
            >
              Layer 1 (Free)
            </TierButton>
            <TierButton
              variant={currentTier === 'layer2' ? 'primary' : 'secondary'}
              size="md"
              onPress={() => switchTier('layer2')}
              testID="switch-layer2"
            >
              Layer 2 (Drama+)
            </TierButton>
            <TierButton
              variant={currentTier === 'layer3' ? 'primary' : 'secondary'}
              size="md"
              onPress={() => switchTier('layer3')}
              testID="switch-layer3"
            >
              Layer 3 (DramaX)
            </TierButton>
          </View>
        </TierCard>

        {/* Typography Showcase */}
        <TierCard variant="default" className="mb-8">
          <TierHeadline size="2xl" weight="bold" className="mb-4" testID="typography-section">
            Typography
          </TierHeadline>

          <TierHeadline size="4xl" weight="bold" className="mb-2">
            Headline 4XL
          </TierHeadline>
          <TierHeadline size="3xl" weight="bold" className="mb-2">
            Headline 3XL
          </TierHeadline>
          <TierHeadline size="2xl" weight="bold" className="mb-2">
            Headline 2XL
          </TierHeadline>
          <TierHeadline size="xl" weight="bold" className="mb-4">
            Headline XL
          </TierHeadline>

          <TierBody size="lg" className="mb-2">
            Body Large: The quick brown fox jumps over the lazy dog.
          </TierBody>
          <TierBody size="base" className="mb-2">
            Body Base: The quick brown fox jumps over the lazy dog.
          </TierBody>
          <TierBody size="sm" className="mb-4">
            Body Small: The quick brown fox jumps over the lazy dog.
          </TierBody>

          <NarratorText size="base" className="italic">
            "Narrator Text: Used for Executive Producer loading quips and episodic framing."
          </NarratorText>
        </TierCard>

        {/* Button Variants */}
        <TierCard variant="default" className="mb-8">
          <TierHeadline size="2xl" weight="bold" className="mb-4">
            Buttons
          </TierHeadline>

          <View className="gap-4">
            <TierButton variant="primary" size="lg" fullWidth testID="button-primary-lg">
              Primary Large
            </TierButton>
            <TierButton variant="primary" size="md" fullWidth>
              Primary Medium
            </TierButton>
            <TierButton variant="primary" size="sm" fullWidth>
              Primary Small
            </TierButton>

            <TierButton variant="secondary" size="md" fullWidth>
              Secondary
            </TierButton>

            <TierButton variant="ghost" size="md" fullWidth>
              Ghost
            </TierButton>

            <TierButton variant="primary" size="md" loading fullWidth>
              Loading State
            </TierButton>

            <TierButton variant="primary" size="md" disabled fullWidth>
              Disabled State
            </TierButton>
          </View>
        </TierCard>

        {/* Card Variants */}
        <View className="gap-8 mb-8">
          <TierCard variant="default" testID="card-default">
            <TierHeadline size="xl" weight="bold" className="mb-2">
              Default Card (All Tiers)
            </TierHeadline>
            <TierBody>
              White background, standard border. Used for Layer 1 content and universal UI elements.
            </TierBody>
          </TierCard>

          <TierCard variant="premium" testID="card-premium">
            <TierHeadline size="xl" weight="bold" color="text-accent-gold" className="mb-2">
              Premium Card (Layer 2+)
            </TierHeadline>
            <TierBody>
              Gold accent border, blush background. Used for Pattern Insights and Drama+ exclusive features.
            </TierBody>
          </TierCard>

          <TierCard variant="editorial" testID="card-editorial">
            <TierHeadline size="xl" weight="bold" color="text-white" className="mb-2">
              Editorial Card (Layer 3)
            </TierHeadline>
            <TierBody color="text-white">
              Black background, white text, Miller Display font. Used for Transformation Summaries and DramaX content.
            </TierBody>
          </TierCard>
        </View>

        {/* Border Radius Showcase */}
        <TierCard variant="default" className="mb-8">
          <TierHeadline size="2xl" weight="bold" className="mb-4">
            Border Radius
          </TierHeadline>
          <TierBody className="mb-4">
            Layer 1: 12px | Layer 2: 8px | Layer 3: 4px
          </TierBody>

          <View className="flex-row gap-4">
            <View className="flex-1 h-24 bg-primary rounded-tier" />
            <View className="flex-1 h-24 bg-accent-gold rounded-tier-sm" />
            <View className="flex-1 h-24 bg-accent-blush rounded-tier-lg" />
          </View>
        </TierCard>

        {/* Traffic Light System */}
        <TierCard variant="default" className="mb-8">
          <TierHeadline size="2xl" weight="bold" className="mb-4">
            Traffic Light System (Tier-Invariant)
          </TierHeadline>

          <View className="flex-row gap-4">
            <View className="flex-1">
              <View className="h-24 bg-safe rounded-tier mb-2" />
              <TierBody align="center" weight="semibold">Safe (Green)</TierBody>
            </View>
            <View className="flex-1">
              <View className="h-24 bg-caution rounded-tier mb-2" />
              <TierBody align="center" weight="semibold">Caution (Yellow)</TierBody>
            </View>
            <View className="flex-1">
              <View className="h-24 bg-danger rounded-tier mb-2" />
              <TierBody align="center" weight="semibold">Danger (Red)</TierBody>
            </View>
          </View>
        </TierCard>

        {/* Transition Timing */}
        <TierCard variant="default" className="mb-8">
          <TierHeadline size="2xl" weight="bold" className="mb-4">
            Transition Timing
          </TierHeadline>
          <TierBody className="mb-4">
            Layer 1: 250ms | Layer 2: 280ms | Layer 3: 280ms (UI), 500ms (reveals)
          </TierBody>

          <TouchableOpacity
            className="transition-tier bg-primary rounded-tier p-6"
            activeOpacity={0.8}
          >
            <TierBody color="text-white" align="center" weight="semibold">
              Press to see transition
            </TierBody>
          </TouchableOpacity>
        </TierCard>

        {/* Font Loading Status */}
        <TierCard variant="default" className="mb-8">
          <TierHeadline size="2xl" weight="bold" className="mb-4">
            Font Loading Status
          </TierHeadline>

          {Platform.OS === 'web' ? (
            <>
              <TierBody className="mb-2">
                ✅ <strong>Web</strong>: Adobe Fonts loaded via Typekit
              </TierBody>
              <TierBody className="mb-2" size="sm" color="text-medium-gray">
                • Layer 1: Inter (system fallback)
              </TierBody>
              <TierBody className="mb-2" size="sm" color="text-medium-gray">
                • Layer 2: Neue Haas Grotesk Display (Bold, 700)
              </TierBody>
              <TierBody className="mb-4" size="sm" color="text-medium-gray">
                • Layer 3: Miller Display (Bold, 700)
              </TierBody>

              <TierBody size="sm" className="italic">
                Check browser DevTools Network tab to verify font loading.
              </TierBody>
            </>
          ) : (
            <>
              <TierBody className="mb-2">
                ⏸️ <strong>Mobile</strong>: Fonts pending purchase
              </TierBody>
              <TierBody size="sm" color="text-medium-gray">
                See: apps/mobile/assets/fonts/README.md for instructions
              </TierBody>
            </>
          )}
        </TierCard>

        {/* Testing Checklist */}
        <TierCard variant="default" className="mb-8">
          <TierHeadline size="2xl" weight="bold" className="mb-4">
            Testing Checklist
          </TierHeadline>

          <TierBody className="mb-2">□ Switch between all 3 tiers</TierBody>
          <TierBody className="mb-2">□ Verify no FOUC (Flash of Unstyled Content)</TierBody>
          <TierBody className="mb-2">□ Check font loading in Network tab</TierBody>
          <TierBody className="mb-2">□ Test keyboard navigation (Tab key)</TierBody>
          <TierBody className="mb-2">□ Verify focus styles (:focus-visible)</TierBody>
          <TierBody className="mb-2">□ Test reduced motion preference</TierBody>
          <TierBody className="mb-2">□ Check high contrast mode (Windows)</TierBody>
          <TierBody className="mb-2">□ Validate WCAG AA contrast ratios</TierBody>
        </TierCard>

        {/* Footer */}
        <View className="mt-8 pt-8 border-t border-gray-300">
          <TierBody size="sm" color="text-medium-gray" align="center">
            Visual Identity System v1.5 | Phase 2 Ready
          </TierBody>
        </View>
      </View>
    </ScrollView>
  );
}
